import { TFunction } from 'i18next'
import { z } from 'zod'

// Rule being triggered because the parent folder is components, should be elements
// eslint-disable-next-line @billy/shine-accounting/consistent-alias-imports -- ../components is this step's own components folder, not src/components
import { AddressingLineFormat } from '../components/AddressingLineSelector/types/frAddressingLinesFormats'
// eslint-disable-next-line @billy/shine-accounting/consistent-alias-imports -- ../components is this step's own components folder, not src/components
import { EInvoicingKycFormType } from '../components/AutoCompanyInformationForm'

export interface CompanyInfo {
  addressingLineFormat: AddressingLineFormat
  accountHolderFirstName: string
  accountHolderLastName: string
  companyAddress: string
  companyCity: string
  companyName: string
  companyZipCode: string
  companyRegistrationNumber: string
  companyLegalUnitRegistrationNumber?: string
}

export type CompanyInfoSchema = z.Schema<CompanyInfo & { formType: EInvoicingKycFormType }>

const NAME_REGEX = /^[A-Za-zÀ-ÖØ-öø-ÿ' -]+$/
const ZIP_CODE_REGEX = /^\d{5}$/
const SIREN_REGEX = /^((\d{9}))$/
const SIRET_REGEX = /^((\d{14}))$/

const NON_EMPTY_TRANSLATION_KEY = 'e_invoice.kyc.step.company_details.form.company_registration.non_empty'

const FORM_TYPE_ENUM = ['auto-full', 'auto-partial', 'manual'] as const

export const getCompanyInfoValidationSchema = (t: TFunction): CompanyInfoSchema => {
  const schema = z
    .object({
      formType: z.enum(FORM_TYPE_ENUM),
      addressingLineFormat: z.enum([AddressingLineFormat.SIREN, AddressingLineFormat.SIREN_SIRET]),
      accountHolderFirstName: z
        .string()
        .nonempty({
          message: t(NON_EMPTY_TRANSLATION_KEY),
        })
        .regex(NAME_REGEX, { message: t('e_invoice.kyc.step.company_details.form.account_holder_name.invalid') }),
      accountHolderLastName: z
        .string({
          // eslint-disable-next-line @typescript-eslint/naming-convention
          required_error: t('e_invoice.kyc.step.company_details.form.company_registration.last_name_non_empty'),
        })
        .nonempty({
          message: t('e_invoice.kyc.step.company_details.form.company_registration.last_name_non_empty'),
        })
        .regex(NAME_REGEX, { message: t('e_invoice.kyc.step.company_details.form.account_holder_name.invalid') }),
      companyAddress: z
        .string({
          // eslint-disable-next-line @typescript-eslint/naming-convention
          required_error: t('e_invoice.kyc.step.company_details.form.company_registration.company_address_non_empty'),
        })
        .nonempty({
          message: t(NON_EMPTY_TRANSLATION_KEY),
        }),
      companyCity: z
        .string({
          // eslint-disable-next-line @typescript-eslint/naming-convention
          required_error: t('e_invoice.kyc.step.company_details.form.company_registration.city_non_empty'),
        })
        .nonempty({
          message: t(NON_EMPTY_TRANSLATION_KEY),
        }),
      companyName: z
        .string({
          // eslint-disable-next-line @typescript-eslint/naming-convention
          required_error: t('e_invoice.kyc.step.company_details.form.company_registration.company_name_non_empty'),
        })
        .nonempty({
          message: t(NON_EMPTY_TRANSLATION_KEY),
        }),
      companyZipCode: z
        .string()
        .length(5, {
          message: t('e_invoice.kyc.step.company_details.form.company_registration.must_be_5_char'),
        })
        .regex(ZIP_CODE_REGEX, {
          message: t('e_invoice.kyc.step.company_details.form.company_registration.must_match_zip_code'),
        }),
      companyRegistrationNumber: z.string({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        required_error: t('e_invoice.kyc.step.company_details.form.company_registration.invalid'),
      }),
    })
    .superRefine((data, ctx) => {
      // We only want to refine for manual forms.
      // This is because we display only 1 input for manual form, but the
      // automatic one fills up
      // * companyRegistrationNumber as SIRET
      // * companyLegalUnitRegistrationNumber AS SIREN
      // so we can toggle between them. On the manual one, we cannot "discover" unless
      // we ask the user for a SIRET always, which is not desired
      if (data.formType !== 'manual') {
        return
      }

      if (
        data.addressingLineFormat === AddressingLineFormat.SIREN &&
        !SIREN_REGEX.test(data.companyRegistrationNumber)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('e_invoice.kyc.step.company_details.form.company_registration.invalid_siren'),
          path: ['companyRegistrationNumber'],
        })
      }

      if (
        data.addressingLineFormat === AddressingLineFormat.SIREN_SIRET &&
        !SIRET_REGEX.test(data.companyRegistrationNumber)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('e_invoice.kyc.step.company_details.form.company_registration.invalid_siret'),
          path: ['companyRegistrationNumber'],
        })
      }
    })

  return schema
}
